export const ENVIRONMENTS = {
  PRODUCTION: 'production',
  DEVELOPMENT: ['development', 'local'],
  PREVIEW: 'preview',
};

export const isDevelopment = ['development', 'preview'].includes(
  process.env.NEXT_PUBLIC_APP_ENV,
);

export const environment = (environments) => {
  environments = Array.isArray(environments) ? environments : [environments];

  return environments.includes(process.env.NEXT_PUBLIC_APP_ENV);
};

export function match(value, cases) {
  return cases[value] ?? cases.default;
}

export function toCamelCase(str) {
  return str
    .replace(/[-_\s]+(.)?/g, (_, char) => (char ? char.toUpperCase() : '')) // Remove separators and capitalize next char
    .replace(/^[A-Z]/, (char) => char.toLowerCase()); // Ensure first char is lowercase
}

export function toSnakeCase(str) {
  return str
    .trim()
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[-_\s]+/g, '_')
    .toLowerCase()
    .replace(/^_+|_+$/g, '');
}

export function toPascalCase(str) {
  return str
    .replace(/[-_\s]+(.)?/g, (_, char) => (char ? char.toUpperCase() : '')) // Remove separators and capitalize next char
    .replace(/^[a-z]/, (char) => char.toUpperCase()); // Ensure first char is uppercase
}

export function toHeadlineCase(str) {
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space between camelCase
    .replace(/[-_]+/g, ' ') // Replace hyphens and underscores with spaces
    .split(/\s+/) // Split on whitespace
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
    .join(' '); // Join with spaces
}

export function randomBetween(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function output(lines = [], title = '', label = '', type = 'log') {
  const maxLength = Math.max(...lines.map((line) => line.length));
  label = label ? label : toHeadlineCase(type);
  const topLine = '─'.repeat(maxLength - label.length - 1) + ` ${label} ─`;
  const bottomLine = '─'.repeat(maxLength + 2);

  /* eslint-disable no-console */
  if (title) {
    console[type](`\n[ ${title} ]`);
  } else {
    console[type]('');
  }
  console[type]('┌' + topLine + '┐');

  lines.map((line) => {
    console[type]('│ ' + line.padEnd(maxLength) + ' │');
  });

  console[type]('└' + bottomLine + '┘');
  /* eslint-enable no-console */
}

// Arithmetic helpers to prevent IEEE 754 precision drift during calculations.
// Perform intermediate operations (sums, multipliers, comparisons) in integer cents.
// Note: Raw variant reads and optimistic UI values bypass these helpers to avoid noise.
export const toCents = (a) => Math.round((+a + Number.EPSILON) * 100);
export const toDollars = (cents) => cents / 100;

export const hasPriceDifference = (full, sale) => toCents(full) !== toCents(sale);

// Formats dollar values into localized display strings. Do not use for arithmetic.
// Automatically cleans up residual float noise and manages decimal visibility.
export function formatPrice(
  number,
  currency = 'USD',
  includeCurrencySymbol = true,
) {
  // Convert string to number if it's a valid numeric string
  if (typeof number === 'string' && !isNaN(Number(number))) {
    number = Number(number);
  }

  if (isNaN(number) || number == null) {
    return new Intl.NumberFormat('en-US', {
      style: includeCurrencySymbol ? 'currency' : 'decimal',
      currency,
    }).format(0);
  }

  const cents = toCents(number);
  const rounded = toDollars(cents);
  const fractionDigits = cents % 100 === 0 ? 0 : 2;

  return new Intl.NumberFormat('en-US', {
    style: includeCurrencySymbol ? 'currency' : 'decimal',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(rounded);
}

export function contrastingColor(backgroundColor) {
  const darkBackgrounds = [
    'black',
    'grey-950',
    'tertiary-navy',
    'slate-gray',
    'gunmetal',
    'mist-dark',
    'blue-sand',
    'steel-blush',
    'slate-denim',
    'pine-light',
    'ocean-to-gunmetal',
  ];

  return darkBackgrounds.includes(backgroundColor) ? 'white' : 'tertiary';
}

export function typographyColors(backgroundColor, textColor = null) {
  if (textColor) {
    return `bg-${backgroundColor} text-${textColor}`;
  }

  return `bg-${backgroundColor} text-${contrastingColor(backgroundColor)}`;
}

// Helper function to truncate HTML content to specified character limit
export function truncateHtmlContent(htmlContent, limit = 100) {
  if (!htmlContent) return '';

  // Convert HTML to plain text and truncate
  const plainText = htmlContent.replace(/<[^>]*>/g, '');

  if (plainText.length <= limit) {
    return htmlContent;
  }

  return plainText.substring(0, limit).trim() + '...';
}

export function iconHasBeenSelected(icon) {
  if (
    icon &&
    icon.library &&
    icon.library !== 'none' &&
    (icon.custom?.icon || icon.heroicons?.icon)
  ) {
    return true;
  }

  return false;
}

export function ctaHasContent(cta) {
  if (cta && cta.destination?.label) {
    return true;
  }

  return false;
}

// Finds the closest matching variant from product.variants based on selectedOptions overlap
export function findMatchingVariant(variant, product, partialMatch = true) {
  if (!variant || !product || !Array.isArray(product.variants)) return null;

  // Helper to compare selectedOptions and return a score
  // Exact matches get score of 10, partial matches get score of 1
  function calculateMatchScore(a, b) {
    if (!Array.isArray(a) || !Array.isArray(b)) return 0;
    let score = 0;

    for (const optA of a) {
      for (const optB of b) {
        if (optA.name !== optB.name) continue;

        const aValue = String(optA.value).toLowerCase();
        const bValue = String(optB.value).toLowerCase();

        // Exact match gets higher score
        if (aValue === bValue) {
          score += 10;
          break;
        }

        // Partial match if enabled gets lower score
        if (
          partialMatch &&
          (aValue.includes(bValue) || bValue.includes(aValue))
        ) {
          score += 1;
          break;
        }
      }
    }

    return score;
  }

  let bestMatch = null;
  let bestScore = -1;

  for (const v of product.variants) {
    const score = calculateMatchScore(
      variant.selectedOptions,
      v.selectedOptions,
    );
    if (score > bestScore) {
      bestScore = score;
      bestMatch = v;
    }
  }

  return bestMatch;
}

export function stableCacheKey(options) {
  return JSON.stringify(options, (_, value) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return Object.keys(value)
        .sort()
        .reduce((carry, key) => {
          carry[key] = value[key];

          return carry;
        }, {});
    }

    return value;
  });
}

export function stringifyMap(map) {
  return Array.from(map.entries())
    .map(([key, value]) => `${key}="${value}"`)
    .join(' ');
}
